<template>
  <div
    class="tiny-mce-wrapper"
    :key="`${tinymceKey}-${footerKey}`"
    :class="{
      'inline': inline,
      'is-focused': isFocused
    }"
  >
    <div
      class="fixed-toolbar-container"
      :class="`fixed-toolbar-${tinymceKey}`"
    >
    </div>
    <!-- TinyMCE inline invocation -->
    <div
      v-if="inline"
      class="inline-wrapper"
      :class="[
        alignmentClass,
        fontSizeClass,
      ]"
    >
      <div
        :id="elementId"
        v-tiny-mce
        class="inline-textarea show-border"
        :aria-label="ariaLabel"
      ></div>
      <span
        v-if="showPlaceholder"
        class="placeholder muted"
        :class="{
          'is-hovered': (
            hoverLabel
            && (
              inputLength
              || isFocused
            )
          ),
          'hoverable': hoverLabel,
        }"
      >
        {{placeholder}}
      </span>
    </div>
    <!-- TinyMCE textarea invocation: -->
    <div
      v-else
      class="block-wrapper"
      :class="`service-${service}`"
    >
      <textarea
        :id="elementId"
        v-tiny-mce
        class="block-textarea show-border"
        :placeholder="placeholder"
      >
      </textarea>
    </div>
    <div class="v-text-field__details pt-1">
      <div class="v-messages theme--light error--text">
        <div class="v-messages__wrapper">
          <div
            v-for="(error, errorIndex) in activeTinymceErrors"
            :key="errorIndex"
            class="v-messages__message py-1"
          >
            {{error.text}}
          </div>
        </div>
      </div>
      <div class="v-counter theme--light">{{charCount}}</div>
    </div>
    <PlaypositAudioRecorder
      v-if="recordMode === 'audio'"
      @success="handleAudioRecorderSuccess"
      @error="handleAudioRecorderError"
      :key="recordMode === 'audio' ? 1 : 0"
      :server-url="$tinymceApi.getServerUrl()"
      :token="$tinymceApi.getToken()"
    />
    <v-dialog
      content-class="full-height white overflow-y-hidden"
      :value="recordMode === 'video'"
      width="500"
      persistent
    >
      <playposit-video-transcoder
        v-if="recordMode === 'video'"
        :api-url="api.getServerUrl()"
        :token="api.getToken()"
        :teacher-id="1270877"
        :bulb-id="bulbId"
        starting-key="upload"
        @success="handleVideoTranscoderSuccess"
        @exit="handleVideoTranscoderExit"
      />
    </v-dialog>
    <!-- Submit TinyMCE response button: -->
    <div :class="['d-flex', 'pb-2', submitTinymceButtonClass]">
      <!-- Renders any delete buttons if applicable: -->
      <template v-if="deleteFunction">
        <v-spacer></v-spacer>
        <v-btn
          class="mr-2"
          :color="brandingColor"
          rounded
          outlined
          @click="removeTinyMCE"
        >
          {{ deletionButtonText }}
        </v-btn>
      </template>
      <v-btn
        v-bind="submitButtonProps"
        :color="brandingColor"
        class="submit-button"
        @click="submitTinyMCE"
        :disabled="(
          !loaded
          || inputLength === 0
        )"
      >
        {{ submissionButtonText }}
      </v-btn>
    </div>
    <!-- Loading screen: -->
    <div
      v-if="showLoadingScreen"
      class="tinymce-loading-screen"
    >
      <v-progress-circular
        indeterminate
        :size="24"
        :width="2"
        color="primary"
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>

/* eslint-disable */

// Libraries / Plugins:
import get from '../node_modules/lodash/get';
import sanitizeHtml from 'sanitize-html';
import throttle from '../node_modules/lodash/throttle';
import party from 'party-js';

import {
  getServerUrl,
} from '@utilities';

// Components:
import Interaction from './mixins/Interaction';
import PlaypositAudioRecorder from '@components/audioRecorder';

import {
  TINY_MCE_TEMPLATES,
  TINY_MCE_CONFIG,
} from './snippets/constants';

export default {
  name: 'tinyMCE',
  components: {
    PlaypositAudioRecorder,
    // PlaypositVideoTranscoder
    // ^^ this vue component is globally registered in the main microservice it is imported in.
    // Might want to consider importing the video-transcoder component here.
  },
  mixins: [ Interaction ],
  props: {
    /**
     * REQUIRED PROPS
     */
    token: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
      default: 'feedback',
      validator: (mode) => [
        'feedback',
        'text-editor',
        'text-editor-update',
      ].includes(mode),
    },
    service: {
      type: String,
      required: false,
      default: 'legacy-dash',
      validator: (service) => [
        'batch',
        'legacy-dash',
        'designer',
        'playlist',
      ].includes(service),
    },
    tinymceKey: {
      type: [
        String,
        Number,
      ],
      required: true,
    },
    footerKey: {
      type: Number,
      required: true,
    },
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * END OF REQUIRED PROPS
     */
    submitFunction: {
      type: Function,
      required: false,
    },
    deleteFunction: {
      type: Function,
      required: false,
    },
    submissionButtonText: {
      type: String,
      required: false,
      default: 'Submit',
    },
    deletionButtonText: {
      type: String,
      required: false,
      default: 'Delete',
    },
    brandingColor: {
      type: String,
      required: false,
      default: '#2196F3'
    },
    updateCommentData: {
      type: Function,
      required: false,
      default: () => { /* ^.^ */},
    },
    attachments: {
      type: Array,
      required: false,
      default: () => [],
    },
    disabledButtons: {
      type: Array,
      required: false,
      default: () => [],
    },
    interaction: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
    alignmentClass: {
      type: String,
      required: false,
    },
    fontSizeClass: {
      type: String,
      required: false,
    },
    ariaLabel: {
      type: String,
      required: false,
    },
    errorText: {
      type: String,
      required: false,
    },
    existingText: {
      type: String,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Enter in some text',
    },
    textModel: {
      type: String,
      required: false,
    },
    initialText: { // Mainly used for feedback in the dash for loading the instructor's initial text
      type: String,
      required: false,
    },
    bulbId: {
      type: Number,
      required: false,
    },
    sectionId: {
      type: Number,
      required: false,
    },
    hoverLabel: {
      type: Boolean,
      required: false,
    },
    enableUpload: {
      type: Boolean,
      required: false,
    },
    apiEnabled: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  directives: {
    tinyMce: {
      async bind(el, binding, vnode) {
        const v = vnode.context;
        const baseServerUrl = getServerUrl();
        const ua = window.navigator.userAgent;
        let iOS = (
          (
            /iPad|iPhone|iPod/.test(window.navigator.platform)
            || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
          ) && !window.MSStream
        );
        if (ua && ua.device) iOS = ['mobile', 'tablet'].includes(ua?.device?.type);
        const webkit = !!ua.match(/WebKit/i);
        const iOSSafari = (
          iOS
          && webkit
          & !ua.match(/CriOS/i)
        );
        await v.$nextTick();
        function initializeTinyMCE() {
          tinymce.init(TINY_MCE_CONFIG.ConfigOptions({
            element: el,
            baseServerUrl,
            virtualNode: v,
            iOSSafari,
            service: v.service,
          }));
        }
        tinymce.remove(`#${el.id}`);
        initializeTinyMCE();
      },
    },
  },
  data: () => ({
    editor: null,
    charCount: 0,
    isFocused: false,
    inputLength: 0,
    recordMode: null,
    tinymceErrors: [],
    loaded: false,
    showLoadingScreen: true,
    uniqueIdentifier: `${Date.now()}-${Math.round(Math.random() * 10000000)}`,
  }),
  computed: {
    localTextModel: {
      get() {
        return this.textModel || '';
      },
      set(textModel) {
        this.$emit('update:textModel', textModel);
      },
    },
    activeTinymceErrors() {
      return this.tinymceErrors.filter((error) => get(error, 'active'));
    },
    showPlaceholder() {
      return (
        this.hoverLabel
        || !this.inputLength
      );
    },
    submitButtonProps() {
      if (this.service === 'dash') return { round: true };
      return {
        rounded: true,
      };
    },
    elementId() {
      return `${this.inline ? 'inline' : 'block'}-textarea-${this.tinymceKey}-${this.uniqueIdentifier}`;
    },
    submitTinymceButtonClass() {
      if (this.service === 'playlist') {
        if (this.mode === 'text-editor') return 'justify-end';
        if (this.mode === 'text-editor-update') return 'justify-start';
      }
      return 'justify-center';
    },
  },
  methods: {
    async scrollToElement(selector) {
      const element = document.querySelector(selector);
      if (!element) return false;
      if (element.offsetTop === 0) {
        this.$timer(25);
        return this.scrollToElement(selector);
      }
      document.body.parentNode.scrollTop = element.offsetTop - 10;
      return true;
    },
    watchEditor: throttle(function watchEditorThrottled(event, ed) {
      const content = ed.getContent();
      if (['<p><br data-mce-bogus="1"></p>', '<p><br></p>', ''].includes(content)) {
        ed.setContent('');
        this.localTextModel = '';
        this.inputLength = 0;
      } else {
        this.localTextModel = content;
        this.inputLength = content.length;
      }
      this.charCount = ed.plugins.charcount.getCount(null, true); // count whitespace
    }, 250, {
      leading: true,
      trailing: true,
    }),
    async submitTinyMCE() {
      try {
        const sanitizedHtmlContent = this.sanitizeUserInput(this.editor.getContent());
        this.loaded = false;
        if (this.mode === 'feedback') {
          const message = await this.$tinymceApi.addFeedback(
            sanitizedHtmlContent,
            this.interaction.interaction_attempt_id,
            this.sectionId
          );
          this.updateCommentData(sanitizedHtmlContent);
          this.loaded = true;
          return message;
        }
        if (this.service === 'playlist') {
          this.submitFunction(sanitizedHtmlContent);
        }
        return null;
      } catch(e) {
        return this.$emit('error', e);
      }
    },
    removeTinyMCE() {
      this.deleteFunction();
    },
    updateTinymceText(text = '') {
      this.localTextModel = text;
      this.editor.setContent(text);
      this.$emit('tinymce-input', text);
      this.inputLength = text.length || 0;
      if (this.editor.plugins.charcount) {
        // We are also counting whitespaces:
        this.charCount = this.editor.plugins.charcount.getCount(null, true);
      }
    },
    handleAudioRecorderSuccess(audioUrl) {
      this.recordMode = null;
      this.editor.execCommand('mceInsertContent', false, TINY_MCE_TEMPLATES.AudioRecordingSuccess(audioUrl));
      this.editor.nodeChanged();
    },
    handleAudioRecorderError(error) {
      this.$emit('error', error);
    },
    async handleVideoTranscoderSuccess(data) {
      await this.$timer(1000);
      this.recordMode = null;
      const [vWidth, vHeight] = get(data, 'aspectRatio', '16:9').split(':');
      const aspectPadding = parseFloat(((parseInt(vHeight, 10) / parseInt(vWidth, 10)) * 100).toFixed(2));
      let url = '/';
      if (this.$inDevEnvironment()) {
        url = this.$getServerUrl();
        if (url === '/') url = `${window.location.origin}/`;
      }
      const playpositVideoId = data?.playpositVideo?.id;
      const html = TINY_MCE_TEMPLATES.VideoRecordingSuccess({ aspectPadding, url, playpositVideoId });
      this.editor.execCommand('mceInsertContent', false, html);
      this.editor.nodeChanged();
    },
    handleVideoTranscoderExit(errorMessage) {
      this.recordMode = null;
      if (errorMessage) this.$emit('error', new Error(errorMessage));
    },
    async initializeApi() {
      const {
        token,
        bulbId,
        sectionId,
        interactionId,
        studentId,
      } = this;
      this.$tinymceApi.init({
        serverUrl: this.$getServerUrl(),
        token,
        bulbId,
        sectionId,
        interactionId,
        studentId,
      });
    },
    sanitizeUserInput(content) {
      return sanitizeHtml(content, {
        allowedTags: [
          'div',
          'p',
          'span',
          'code',
          'a',
          'table',
          'strong',
          'sub',
          'sup',
          'em',
          'ul',
          'ol',
          'li',
          'audio',
          'video',
          'source',
          'img',
          'table',
          'tr',
          'td',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'h7',
        ],
        allowedAttributes: {
          img: [ 'alt', 'title', 'src', 'height', 'width' ],
          div: [ 'style' ],
          p: [ 'style' ],
          span: [ 'style' ],
          a: [ 'title', 'style', 'href', 'target' ],
          ul: [ 'style' ],
          ol: [ 'style' ],
          audio: [ 'style', 'controls' ],
          source: [ 'src', 'type' ],
          table: [ 'style', 'border' ],
          li: [ 'style' ],
          sub: [ 'style' ],
          sup: [ 'style' ],
          h1: [ 'style' ],
          h2: [ 'style' ],
          h3: [ 'style' ],
          h4: [ 'style' ],
          h5: [ 'style' ],
          h6: [ 'style' ],
          h7: [ 'style' ],
        },
        allowedClasses: { // for whenever we do support Audio && Video upload.
          div: ['mux-video-wrapper'],
          img: ['responsive'],
          audio: [ 'playposit-audio-upload' ],
        }
      });
    },
    /**
     * Come on... everyone needs a little party-time, right? <^.^>
     */
    initializePartyTime() {
      const invocationMethod = (Math.random() > 0.5) ? 'sparkles' : 'confetti';
      const partyInitElement = document.getElementsByClassName(`${(this.inline) ? 'inline' : 'block'}-wrapper`)[(this?.serivce === 'legacy-dash') ? 1 : 0];
      party[invocationMethod](partyInitElement, {
        count: party.variation.range(5, 20),
        size: party.variation.range(0.6, 1.4),
      });
    },
  },
  watch: {
    token: {
      immediate: true,
      async handler(newValue, oldValue) { // eslint-disable-line
        if (!!newValue) {
          if (Math.random() <= 0.01) {
            setTimeout(() => {
              this.initializePartyTime();
            }, 1500);
          }
          if (this.apiEnabled) await this.initializeApi();
          this.loaded = true;
        }
      },
    },
  },
  beforeDestroy() {
    tinymce.remove(`#${this.elementId}`);
  },
};
</script>

<style lang="scss">
  .tiny-mce-wrapper {
    position: relative;
    max-width: 100%;
    height: 100%;
    overflow-y: scroll;

    &.inline {
      min-height: 38px;
    }

    &:not(.underline-only-on-focus) .inline-textarea.show-border {
      border-color: #9e9e9e;
    }

    &.underline-only-on-focus.is-focused .inline-textarea.show-border {
      border-color: #9e9e9e;
    }

    &.highlight-error .inline-textarea {
      border-color: #ff5252 !important;
    }

    .inline-wrapper,
    .block-wrapper {
      position: relative;
    }

    .inline-wrapper {
      &.align-with-radio {
        margin-top: 6px;

        .placeholder {
          top: 5px;
        }
      }

      &.align-with-checkbox {
        margin-top: 3px;
      }

      .placeholder {
        top: 5px;
        position: absolute;
        pointer-events: none;
        font-size: 14px;
        transition: all .1s linear;
        z-index: 2;
        width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;

        &.hoverable {
          top: 3px;
          font-size: 16px;
        }

        &.is-hovered {
          top: -15px;
          font-size: 12px;
        }
      }
    }

    .block-wrapper .placeholder {
      position: absolute;
      top: 86px;
      left: 12px;
      pointer-events: none;
      font-size: 12px;
    }

    .block-wrapper.service-playlist .tox.tox-tinymce {
      border-radius: 7px;
    }

    .fixed-toolbar-container {
      position: absolute;
      bottom: 100%;
      height: 69px;
      width: 100%;
      z-index: 10000;
      display: none;

      &.toolbar-open {
        display: block;
      }
    }

    .mce-tinymce {
      box-sizing: border-box;

      &:not(.mce-tinymce-inline) {
        min-height: 220px;
      }

      &.mce-container .mce-menubar {
        display: none;
      }
    }

    .mce-tinymce-inline.mce-container {
      height: 50% !important;
      position: absolute;
      top: auto !important;
      bottom: 0;

      &.extended {
        height: 100% !important;

        .mce-menubar {
          display: block;
        }
      }
    }

    .mce-container-body.mce-abs-layout {
      height: 100% !important;
      bottom: 0;
    }

    .mce-toolbar-grp.mce-last {
      top: auto !important;
      bottom: 0;
      left: -2px !important;

      >.mce-container-body {
        margin-top: 3px;
      }
    }

    .alert.warning > div {
      white-space: normal;
    }

    .inline-textarea {
      padding: 4px 0;
      border-bottom: 1px solid transparent;
      overflow-y: auto;
      overflow-x: hidden;
      cursor: text;
      min-height: 30px;
      line-height: 1.5;

      &.mce-edit-focus {
        outline: 0;
        border-color: var(--branding-active-hex);
      }
    }
  }

  i.mce-i-more_vert:before {
    content: "\e5d4";
    font-family: 'Material Icons';
    font-style: normal;
    font-weight: normal;
    text-decoration: inherit;
    color: #000;
    font-size: 1.5em;
    padding-right: 0.5em;
    position: absolute;
    top: 20%;
    left: 0;
  }

  .tox .tox-dialog {
    max-width: 630px !important;
  }

  .d-flex .submit-button {
    flex: 0 1 auto !important;
    width: 30%;
    color: white;
  }

  .tinymce-loading-screen {
    position: absolute;
    background: white;
    z-index: 99999;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
</style>
